import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import home from "../pro-home.module.css";
import { ACTOR_TYPES, AD_STATIC_TYPES, PRODUCT_PHOTO_TYPES, useOwnPictures } from "./home-pictures";
import { MakeTile, type MakeTileId } from "./make-tiles";
import { Recent } from "./Recent";
import classes from "./simple-sections.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/simple/images";

/** The prototype's image tiles, in its order: the flows the app has, then two to come. */
const MAKE_IMAGE: readonly MakeTileId[] = ["photos", "static", "actor", "tweak", "layers"];

function MakeImage({ teamId, brandId }: { teamId: string; brandId: string }) {
  // Each tile's newest picture, its flow's own results read (Ad static's is Home's card's).
  const own: Partial<Record<MakeTileId, ReturnType<typeof useOwnPictures>>> = {
    photos: useOwnPictures(teamId, brandId, PRODUCT_PHOTO_TYPES),
    static: useOwnPictures(teamId, brandId, AD_STATIC_TYPES),
    actor: useOwnPictures(teamId, brandId, ACTOR_TYPES),
  };
  return (
    // The prototype gives its tiles no heading: the page's title is theirs.
    <section className={classes.lone} aria-label="Make an image">
      <div className={classes.five}>
        {MAKE_IMAGE.map((id) => (
          <MakeTile key={id} id={id} teamId={teamId} brandId={brandId} own={own[id]} />
        ))}
      </div>
    </section>
  );
}

/**
 * Simple's Images (spec ui-perf/simple-images): the Created AI Simple prototype's `VIEWS.images`,
 * its ways to make an image and the brand's recent images.
 */
export function SimpleImages() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  return (
    <div className={classes.page}>
      <h1 className={classes.title}>Images</h1>
      <p className={classes.lead}>Product photos, ad statics and actors.</p>
      <MakeImage teamId={teamId} brandId={brandId} />
      <Recent
        teamId={teamId}
        brandId={brandId}
        medium="image"
        title="Recent images"
        empty="No images yet. The ones the brand makes show up here."
        links={
          // Simple's Library on its Images (spec ui-perf/simple-library).
          <Link
            to="/teams/$teamId/brands/$brandId/simple/library"
            params={{ teamId, brandId }}
            search={{ kind: "image" }}
            className={home.subtle}
          >
            All images <ArrowRight size={14} aria-hidden />
          </Link>
        }
      />
    </div>
  );
}
